import { ReactNode, useMemo } from 'react';
import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { Value } from 'baseui/select';
import {
  DataSourceLogic,
  LoadToVectorData,
  LoadingJobData,
  OneColumnMapping,
  OneColumnMappingDataSourceColumnSource,
  OneColumnMappingMappingWidgetSource,
  SourceType,
} from '@tigergraph/tools-models/loading-job';
import { LuFileText } from 'react-icons/lu';
import { EmbeddingAttribute } from '@tigergraph/tools-models';
import { SourceSelect } from '@/components/schemaDesigner/attributeEditorContainer/attributeEditor/AttributeSelect';
export interface EmbeddingAttributeSourceSelectProps {
  attribute: EmbeddingAttribute;
  loadingJob: LoadingJobData | undefined;
  loadingStatement: LoadToVectorData;
  columnMapping: OneColumnMapping | undefined;
  disabled: boolean;
  onChangeSources: (
    mappingSource: OneColumnMappingDataSourceColumnSource | OneColumnMappingMappingWidgetSource
  ) => void;
}

export function EmbeddingAttributeSourceSelect({
  attribute,
  loadingJob,
  loadingStatement,
  columnMapping,
  disabled,
  onChangeSources,
}: EmbeddingAttributeSourceSelectProps) {
  const [css, theme] = useStyletron();
  const sourceOptions = useMemo(() => {
    const options = [] as {
      id: string;
      name: string;
      label: ReactNode;
      mapping: OneColumnMappingDataSourceColumnSource | OneColumnMappingMappingWidgetSource;
    }[];
    if (loadingJob?.dataSet.dataSchema) {
      loadingJob.dataSet.dataSchema.forEach((item, index) => {
        if (item.name) {
          options.push({
            id: `data_source_column_${index}`,
            name: item.name,
            label: (
              <div
                className={css({
                  display: 'flex',
                  columnGap: '4px',
                  alignItems: 'center',
                })}
              >
                <span>
                  <LuFileText size={12} color={theme.colors['dropdown.icon']} />
                </span>
                <span>{item.name}</span>
              </div>
            ),
            mapping: {
              // @ts-ignore
              type: SourceType.DataSourceColumn,
              columnIndex: index,
            },
          });
        }
      });
    }

    return options;
  }, [css, loadingJob?.dataSet.dataSchema, theme.colors]);

  const options = useMemo(() => {
    const options = [...sourceOptions];
    options.push({
      id: 'token_function_split',
      name: 'Split()',
      label: (
        <div
          className={css({
            display: 'flex',
            columnGap: '4px',
            alignItems: 'center',
          })}
        >
          <span>
            <LuFileText size={12} color={theme.colors['dropdown.icon']} />
          </span>
          <span>Split()</span>
        </div>
      ),
      mapping: {
        // @ts-ignore
        type: SourceType.DataSourceColumn,
        // Reset the column mapping to default when user click split()
        columnIndex: -2,
        isSplit: true,
        splitSeparator: '',
      },
    });
    return options;
  }, [css, sourceOptions, theme.colors]);

  const sepOptions = useMemo(() => {
    return DataSourceLogic.separators.map((item) => ({
      id: item[1],
      name: item[2],
      label: item[2],
    }));
  }, []);

  const source = useMemo<Value>(() => {
    // @ts-ignore
    if (columnMapping?.sourceType === SourceType.DataSourceColumn) {
      if (columnMapping?.isSplit) {
        return [{ id: 'token_function_split' }];
      } else {
        return [{ id: `data_source_column_${columnMapping.index}` }];
      }
      // @ts-ignore
    } else if (columnMapping?.sourceType === SourceType.MappingWidget) {
      return [{ id: `token_function_${columnMapping.index}` }];
    } else {
      return [];
    }
  }, [columnMapping]);

  const splitValue = useMemo<Value>(() => {
    if (
      // @ts-ignore
      columnMapping?.sourceType === SourceType.DataSourceColumn &&
      columnMapping?.isSplit &&
      Number(columnMapping.index) >= 0
    ) {
      return [{ id: `data_source_column_${columnMapping.index}` }];
    } else if (
      // @ts-ignore
      columnMapping?.sourceType === SourceType.MappingWidget &&
      Number(columnMapping.index) >= 0
    ) {
      return [{ id: `token_function_${columnMapping.index}` }];
    } else {
      return [];
    }
  }, [columnMapping]);

  return (
    <div>
      <SourceSelect
        value={source}
        options={options}
        onChange={({ value }) => {
          if (value.length === 0) {
            onChangeSources({
              // @ts-ignore
              type: SourceType.DataSourceColumn,
              columnIndex: -1,
            });
          } else {
            onChangeSources(value[0].mapping);
          }
        }}
        disabled={true}
        placeholder={disabled ? 'Not support' : 'Select source'}
      />
      {columnMapping?.isSplit && (
        <>
          <SourceSelect
            value={splitValue}
            options={sourceOptions}
            onChange={({ value }) => {
              if (value.length === 0) {
                onChangeSources({
                  // @ts-ignore
                  type: SourceType.DataSourceColumn,
                  // Reset the column mapping to default when user clear the split separator
                  columnIndex: -2,
                  isSplit: false,
                  splitSeparator: columnMapping.splitSeparator,
                });
              } else {
                onChangeSources({
                  // @ts-ignore
                  type: SourceType.DataSourceColumn,
                  columnIndex: value[0].mapping.columnIndex,
                  isSplit: true,
                  splitSeparator: columnMapping.splitSeparator || ',',
                });
              }
            }}
            disabled={disabled}
            placeholder={disabled ? 'Not support' : 'Select source'}
          />
          <SourceSelect
            value={columnMapping?.splitSeparator ? [{ id: columnMapping.splitSeparator }] : []}
            options={sepOptions}
            onChange={({ value }) => {
              const newColumnMapping = {
                // @ts-ignore
                type: SourceType.DataSourceColumn,
                columnIndex: columnMapping.index ?? -2,
                isSplit: true,
                splitSeparator: '',
              };
              if (value.length > 0) {
                newColumnMapping.splitSeparator = String(value[0].id);
              }
              onChangeSources(newColumnMapping);
            }}
            disabled={disabled}
            placeholder={disabled ? 'Not support' : 'Select separator'}
          />
        </>
      )}
    </div>
  );
}
